import { productImageUrl } from '../apiConfig';
import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import Header from './header';
import SearchBar from './SearchBar';
import './SearchResultsPage.css';

const SEARCH_PAGE_SIZE = 12;

const formatDisplayText = (value) =>
  value
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const query = (searchParams.get('q') || '').trim();
  const city = (searchParams.get('city') || '').trim().toLowerCase();
  const page = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);
  const formattedCity = city ? formatDisplayText(city) : '';

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(Boolean(query));
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    setActiveFilter('all');
  }, [query, city]);

  useEffect(() => {
    if (!query) {
      setResults([]);
      setLoading(false);
      setError(null);
      setTotalPages(1);
      setTotalResults(0);
      return;
    }

    const controller = new AbortController();

    const fetchSearchResults = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          query,
          page: page.toString(),
          limit: SEARCH_PAGE_SIZE.toString(),
        });

        if (city) {
          params.set('city', city);
        }

        const response = await fetch(`/api/search?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }

        const data = await response.json();

        if (data.results && data.pagination) {
          setResults(Array.isArray(data.results) ? data.results : []);
          setTotalPages(data.pagination.totalPages || 1);
          setTotalResults(data.pagination.totalResults || 0);
        } else if (Array.isArray(data)) {
          setResults(data);
          setTotalResults(data.length);
          setTotalPages(Math.max(Math.ceil(data.length / SEARCH_PAGE_SIZE), 1));
        } else {
          setResults([]);
          setTotalPages(1);
          setTotalResults(0);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        console.error('Search error:', err);
        setResults([]);
        setTotalPages(1);
        setTotalResults(0);
        setError('An error occurred while searching. Please try again.');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchSearchResults();

    return () => controller.abort();
  }, [query, city, page]);

  const handleSearch = (newQuery) => {
    const trimmedQuery = newQuery.trim();

    if (!trimmedQuery) {
      return;
    }

    const params = new URLSearchParams({
      q: trimmedQuery,
      page: '1',
    });

    if (city) {
      params.set('city', city);
    }

    navigate(`/search-results?${params.toString()}`);
  };

  const handleCityChange = (e) => {
    const newCity = e.target.value;
    const params = new URLSearchParams(location.search);

    if (newCity) {
      params.set('city', newCity);
    } else {
      params.delete('city');
    }

    params.set('page', '1');
    navigate(`/search-results?${params.toString()}`);
  };

  const handlePageChange = (pageNum) => {
    const params = new URLSearchParams(location.search);
    params.set('page', pageNum.toString());
    navigate(`/search-results?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredResults = activeFilter === 'all' 
    ? results 
    : results.filter(item => item.type === activeFilter);

  const productCount = results.filter(item => item.type === 'product').length;
  const storeCount = results.filter(item => item.type === 'store').length;
  const visibleResultsCount = filteredResults.length;
  const safeTotalPages = Math.max(totalPages, 1);
  const summaryText = !query
    ? 'Ready to search'
    : loading
      ? 'Searching...'
      : error
        ? 'Search unavailable'
        : `${totalResults} total ${totalResults === 1 ? 'result' : 'results'}${formattedCity ? ` in ${formattedCity}` : ''}`;
  const toolbarSubtitle = query
    ? `${formattedCity ? `Browsing ${formattedCity}. ` : ''}Use the controls below to refine the results without losing screen space.`
    : 'Jump straight into products, stores, and services from local businesses.';
  const resultsMeta = !query
    ? 'Enter a search term to start browsing.'
    : loading
      ? `Looking for "${query}"...`
      : error
        ? 'Search is currently unavailable.'
        : totalResults === 0
          ? 'No matches yet. Try another term or adjust the city filter.'
          : `Showing ${visibleResultsCount} ${visibleResultsCount === 1 ? 'result' : 'results'} on page ${page} of ${safeTotalPages}.`;

  return (
    <>
      <Header />
      <div className="search-results-page">
        <section className="search-toolbar-section">
          <div className="search-results-container search-results-container--toolbar">
            <div className="search-toolbar">
              <div className="search-toolbar-header">
                <div className="search-toolbar-title-group">
                  <p className="search-toolbar-label">StoreToYou Search</p>
                  <h1 className="search-toolbar-title">
                    {query ? `Results for "${query}"` : 'Search local products and stores'}
                  </h1>
                  <p className="search-toolbar-subtitle">
                    {toolbarSubtitle}
                  </p>
                </div>
                <div className="search-toolbar-summary">
                  {summaryText}
                </div>
              </div>

              <div className="search-toolbar-controls">
                <div className="search-toolbar-search">
                  <SearchBar initialQuery={query} onSearch={handleSearch} />
                </div>
                <div className="filter-section search-toolbar-city-filter">
                  <label htmlFor="city-filter">City</label>
                  <select 
                    id="city-filter" 
                    value={city} 
                    onChange={handleCityChange}
                    className="city-select"
                  >
                    <option value="">All Cities</option>
                    <option value="kelowna">Kelowna</option>
                    <option value="vancouver">Vancouver</option>
                    <option value="victoria">Victoria</option>
                    {/* Add more cities as needed */}
                  </select>
                </div>
              </div>

              <div className="search-filters">
                <button 
                  type="button"
                  className={`filter-button ${activeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All Results ({totalResults})
                </button>
                <button 
                  type="button"
                  className={`filter-button ${activeFilter === 'product' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('product')}
                >
                  Products ({productCount})
                </button>
                <button 
                  type="button"
                  className={`filter-button ${activeFilter === 'store' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('store')}
                >
                  Stores ({storeCount})
                </button>
              </div>

              <p className="search-toolbar-results-meta">{resultsMeta}</p>
            </div>
          </div>
        </section>

        <div className="search-results-container search-results-container--content">
          <div className="search-results">
            {!query ? (
              <div className="search-empty-state">
                <h2>Start with a search</h2>
                <p>Search for products, stores, or services, then narrow the results by city or tab.</p>
              </div>
            ) : loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Searching for "{query}"...</p>
              </div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : totalResults === 0 || filteredResults.length === 0 ? (
              <div className="no-results">
                <h2>
                  {activeFilter === 'all'
                    ? `No results found for "${query}"`
                    : `No ${activeFilter} results found for "${query}"`}
                </h2>
                <p>
                  {activeFilter === 'all'
                    ? 'Try different keywords or check for typos.'
                    : 'Try switching tabs or adjusting the city filter.'}
                </p>
              </div>
            ) : (
              <>
                <div className="results-grid">
                  {filteredResults.map(item => (
                    <ResultCard key={`${item.type}-${item.id}`} item={item} />
                  ))}
                </div>
                
                {/* Pagination controls */}
                {totalPages > 1 && (
                  <div className="pagination">
                    {page > 1 && (
                      <button 
                        type="button"
                        className="page-button prev"
                        onClick={() => handlePageChange(page - 1)}
                      >
                        &laquo; Previous
                      </button>
                    )}
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      // Display only a reasonable number of page buttons
                      .filter(pageNum => 
                        pageNum === 1 || 
                        pageNum === totalPages || 
                        (pageNum >= page - 2 && pageNum <= page + 2)
                      )
                      .map((pageNum, index, array) => (
                        <React.Fragment key={pageNum}>
                          {index > 0 && array[index - 1] !== pageNum - 1 && (
                            <span className="page-ellipsis">...</span>
                          )}
                          <button
                            type="button"
                            className={`page-button ${pageNum === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </React.Fragment>
                      ))
                    }
                    
                    {page < totalPages && (
                      <button 
                        type="button"
                        className="page-button next"
                        onClick={() => handlePageChange(page + 1)}
                      >
                        Next &raquo;
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Enhanced Result Card Component with image and store info
const ResultCard = ({ item }) => {
  const navigate = useNavigate();
  const displayTitle = item.title || item.name || 'Untitled';
  
  const handleCardClick = () => {
    if (item.type === 'product') {
      if (item.store_url_name && item.city) {
        navigate(`/${item.city.toLowerCase()}/${item.store_url_name}/product/${item.id}`);
      } else {
        navigate(`/product/${item.id}`);
      }
    } else if (item.type === 'store') {
      if (item.url_name) {
        navigate(`/${item.city?.toLowerCase() || 'kelowna'}/${item.url_name}`);
      } else {
        navigate(`/${item.city?.toLowerCase() || 'kelowna'}/${item.id}`);
      }
    }
  };

  return (
    <div className={`result-card ${item.type}-card`} onClick={handleCardClick}>
      <div className="result-image">
        {item.image_url ? (
          <img 
            src={productImageUrl(item.image_url)} 
            alt={displayTitle} 
            className="result-img"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/300x225?text=No+Image";
            }}
          />
        ) : (
          <div className="placeholder-image">
            {item.type === 'product' ? '📦' : '🏪'}
          </div>
        )}
      </div>
      
      <div className="result-content">
        {item.type === 'product' && (
          <div className="store-info">
            <div className="store-logo">
              {item.store_profile_image_url ? (
                <img 
                  src={item.store_profile_image_url} 
                  alt={item.store_name || 'Store'} 
                  className="store-logo-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    // Show first letter when image fails
                    e.target.parentNode.innerHTML = (item.store_name || 'S').charAt(0).toUpperCase();
                  }}
                />
              ) : (
                <span>{(item.store_name || 'S').charAt(0).toUpperCase()}</span>
              )}
            </div>
            <Link 
              to={`/${item.city?.toLowerCase() || 'kelowna'}/${item.store_url_name || ''}`} 
              className="store-name"
              onClick={(e) => {
                if (item.store_url_name) {
                  e.stopPropagation();
                } else {
                  e.preventDefault();
                }
              }}
            >
              {item.store_name || 'Store'}
            </Link>
            <div className="store-location">{item.city || ''}</div>
          </div>
        )}
        
        <h3 className="result-title">{displayTitle}</h3>
        
        {item.description && (
          <p className="result-description">
            {item.description.length > 100 
              ? `${item.description.substring(0, 100)}...` 
              : item.description}
          </p>
        )}
        
        <div className="result-footer">
          {item.type === 'product' && item.price && (
            <div className="result-price">{item.price_type === 'from' ? 'From ' : ''}${parseFloat(item.price).toFixed(2)}{item.availability === 'out_of_stock' && <span> · Sold out online</span>}{item.availability === 'preorder' && <span> · Preorder</span>}</div>
          )}
          
          {item.type === 'store' && (
            <div className="store-location-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{item.city || 'Local'}</span>
            </div>
          )}
          
          <div className="view-details">
            {item.type === 'product' ? 'View Product' : 'Visit Store'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
